import dayjs from 'dayjs';
import { TIME_24_HOUR } from '@application/utilities/date-formats';
import { formatRegistrationDate } from '@application/utilities/registration-date-time';
const isBooleanLike = (value) => {
    if (typeof value === 'boolean')
        return true;
    const normalized = String(value).toLowerCase();
    return normalized === 'true' || normalized === 'false';
};
const toBooleanLikeLabel = (value, t) => {
    if (typeof value === 'boolean') {
        return value ? t('common.yes') : t('common.no');
    }
    return String(value).toLowerCase() === 'true' ? t('common.yes') : t('common.no');
};
const formatDateFieldValue = (value, locale) => {
    const parsed = value instanceof Date
        ? dayjs(value)
        : typeof value === 'string' && value.trim()
            ? dayjs(value)
            : null;
    if (!parsed?.isValid())
        return null;
    return formatRegistrationDate({ startDate: parsed.format('YYYY-MM-DD') }, locale);
};
const formatTimeFieldValue = (value) => {
    const parsed = value instanceof Date
        ? dayjs(value)
        : typeof value === 'string' && value.trim()
            ? dayjs(value)
            : null;
    if (!parsed?.isValid())
        return null;
    return parsed.format(TIME_24_HOUR);
};
const getSubFieldRole = (subField) => subField.subFieldRole ?? subField.SubFieldRole ?? subField.role;
const getPersonDisplayName = (person, subFieldDefinitions) => {
    const nameFieldIds = subFieldDefinitions
        .filter((def) => {
        const role = getSubFieldRole(def);
        return role === 'firstName' || role === 'middleName' || role === 'lastName';
    })
        .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
        .map((def) => def.id);
    return nameFieldIds
        .map((id) => person[id])
        .filter((part) => typeof part === 'string' && part.trim() !== '')
        .join(' ');
};
// The Additional-Attendees field's raw value is `{ people: [...] }`, keyed per person by sub-field
// id - not a primitive, so the generic formatter below can't stringify it meaningfully (it would
// fall through to `String(value)` -> "[object Object]"). Build a "Jane Doe, Sam Doe" summary from
// the First/Last name sub-fields instead, falling back to "Person 1, Person 2" if names aren't set.
export const formatAdditionalAttendeesSummaryValue = (people, subFieldDefinitions, t) => {
    return buildAdditionalAttendeesSummaryPeople(people, subFieldDefinitions, t)
        .map((person) => person.title)
        .join(', ');
};
export const buildAdditionalAttendeesSummaryPeople = (people, subFieldDefinitions, t, locale) => {
    const sortedDefs = [...subFieldDefinitions].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
    const nameFieldIds = new Set(sortedDefs
        .filter((def) => {
        const role = getSubFieldRole(def);
        return role === 'firstName' || role === 'middleName' || role === 'lastName';
    })
        .map((def) => def.id));
    const hasNameFields = nameFieldIds.size > 0;
    return people
        .map((person, index) => {
        const fullName = getPersonDisplayName(person, sortedDefs);
        const title = fullName ||
            t('eventRegistration.form.additionalAttendeesPersonLabel', { number: index + 1 });
        const lines = sortedDefs
            .filter((def) => !hasNameFields || !nameFieldIds.has(def.id))
            .map((def) => {
            const raw = person[def.id];
            if (raw === undefined || raw === null || raw === '')
                return null;
            const formatted = formatSummaryFieldValue(raw, t, def.fieldOptions || [], {
                fieldType: def.fieldType,
                locale
            });
            if (!formatted)
                return null;
            return hasNameFields ? formatted : `${def.label}: ${formatted}`;
        })
            .filter((line) => Boolean(line));
        const hasAnyValue = sortedDefs.some((def) => {
            const raw = person[def.id];
            return raw !== undefined && raw !== null && raw !== '';
        });
        if (!hasAnyValue)
            return null;
        return { title, lines };
    })
        .filter((person) => person !== null);
};
export const formatSummaryFieldValue = (value, t, options = [], config = {}) => {
    const { fieldType, locale } = config;
    if (value === undefined || value === null || value === '')
        return '';
    if (Array.isArray(value)) {
        return value
            .map((item) => formatSummaryFieldValue(item, t, options, config))
            .filter(Boolean)
            .join(', ');
    }
    if (fieldType === '4') {
        const formattedDate = formatDateFieldValue(value, locale || 'en-US');
        if (formattedDate)
            return formattedDate;
    }
    if (fieldType === '5') {
        const formattedTime = formatTimeFieldValue(value);
        if (formattedTime)
            return formattedTime;
    }
    if (fieldType === '19') {
        const isAcknowledged = value === true || String(value).toLowerCase() === 'true';
        return isAcknowledged
            ? t('eventRegistration.form.undertakingSummaryAgree')
            : t('eventRegistration.form.undertakingSummaryDecline');
    }
    const matched = options.find((opt) => String(opt.value) === String(value));
    if (matched?.label)
        return matched.label;
    if (isBooleanLike(value)) {
        return toBooleanLikeLabel(value, t);
    }
    return String(value);
};
